import { __assign } from 'tslib';
import React__default, { useState, useCallback, useEffect } from 'react';
import { useTheme, Box } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { animated, useSpring, config } from 'react-spring';
import KButton from '../../KButtons/index.js';
import SanitizeHtml from '../../SanitizeHtml/index.js';
import { NotificationDuration, NotificationType } from '../models.js';
import StyledNotification, { classes } from './index.styled.js';

var AnimatedStyledNotification = animated(StyledNotification);
var converDurationInMs = function (duration) {
    switch (duration) {
        case NotificationDuration.Short:
            return 10 * 1000;
        case NotificationDuration.Regular:
            return 20 * 1000;
        case NotificationDuration.Long:
            return 30 * 1000;
        case NotificationDuration.Persist:
            return undefined;
        default:
            return duration;
    }
};
var Notification = function (_a) {
    var className = _a.className, style = _a.style, sx = _a.sx, id = _a.id, _b = _a.testId, testId = _b === void 0 ? "Notification" : _b, children = _a.children, content = _a.content, _c = _a.dismissable, dismissable = _c === void 0 ? true : _c, _d = _a.onDismiss, onDismiss = _d === void 0 ? function () { } : _d, actions = _a.actions, size = _a.size, _e = _a.duration, duration = _e === void 0 ? NotificationDuration.Regular : _e, notificationId = _a.notificationId, _f = _a.type, type = _f === void 0 ? NotificationType.Primary : _f;
    var theme = useTheme();
    var t = useTranslation("refront-components").t;
    var _g = useState(false), hover = _g[0], setHover = _g[1];
    var _h = useState(false), dismissed = _h[0], setDismissed = _h[1];
    var durationInMs = converDurationInMs(duration);
    var _j = useSpring(function () { return ({ opacity: 0, scale: 0.8 }); }), pop = _j[0], popApi = _j[1];
    var endAnimationDuration = theme.transitions.duration.shortest;
    var endFromAnimation = useCallback(function () {
        popApi.start({
            opacity: 0,
            scale: 0,
            config: { duration: endAnimationDuration },
            onRest: function () { return onDismiss(notificationId); }
        });
    }, [endAnimationDuration, notificationId, onDismiss, popApi]);
    var _k = useSpring(function () { return ({
        width: "0%",
        onRest: endFromAnimation
    }); }), width = _k[0].width, durationApi = _k[1];
    var handleClose = useCallback(function () {
        setDismissed(true);
        durationApi.start({ width: "100", config: { duration: endAnimationDuration } });
    }, [durationApi, endAnimationDuration]);
    useEffect(function () {
        popApi.start({ opacity: 1, scale: 1, config: config.gentle });
        if (durationInMs) {
            durationApi.start({ width: "100%", config: { duration: durationInMs } });
        }
    }, [durationApi, durationInMs, popApi]);
    useEffect(function () {
        if (hover && !dismissed) {
            durationApi.pause();
        }
        else {
            durationApi.resume();
        }
    }, [hover, durationApi, dismissed]);
    return (React__default.createElement(AnimatedStyledNotification, { sx: sx, className: clsx("Notification", className, "Notification-id-".concat(notificationId)), "data-testid": testId, id: id, size: size, type: type, style: __assign(__assign({}, style), pop), onMouseEnter: function () { return setHover(true); }, onMouseLeave: function () { return setHover(false); }, onFocusCapture: function () { return setHover(true); }, onBlurCapture: function () { return setHover(false); } },
        durationInMs !== undefined && (React__default.createElement(Box, { "data-testid": "Notification-progress", className: clsx(classes.progress, "Notification-progress") },
            React__default.createElement(animated.div, { className: classes.progressBar, style: { width: width } }))),
        React__default.createElement(Box, { className: classes.content },
            !!content && React__default.createElement(SanitizeHtml, { html: content }),
            children),
        React__default.createElement(Box, { className: classes.actions },
            dismissable && (React__default.createElement(KButton, { testId: "Notification-dismiss", className: classes.dismiss, onClick: handleClose, variant: "text", size: "small" }, t("components.notificationCenter.notification.actions.dismiss.label"))),
            React__default.createElement(Box, null, actions))));
};

export { Notification as default };
//# sourceMappingURL=index.js.map
